import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import {
  checkAuthStatus,
  fetchFlow360Cases,
  fetchFlow360Folders,
  fetchFlow360Geometries,
  fetchFlow360SurfaceMeshes,
  fetchFlow360VolumeMeshes,
  fetchFullConfig,
  fetchImportProgress,
  getFlow360AccessToken,
  getFlow360UserEmail,
  importFlow360Data,
} from "../../flow360/api";
import type {
  Flow360Case,
  Flow360Folder,
  Flow360Geometry,
  Flow360SurfaceMesh,
  Flow360VolumeMesh,
} from "../../flow360/types";
import { Icons } from "../thread-v4/icons";
import type { SurfaceProps } from "./ThreadV5Surfaces";
import {
  estimateFlow360PanelRunDifficulty,
  formatBytes,
  formatGigabytes,
  formatPanels,
  type Flow360PanelRunEstimate,
  type PanelRunRisk,
} from "./ThreadV5Flow360Estimate";
import {
  createFilamentModelPlan,
  extractFilamentInputs,
  type FilamentBackendTarget,
  type FilamentDecimationStrategy,
  type FilamentInputControl,
  type FilamentModelPlan,
} from "./ThreadV5FilamentModel";
import {
  artifactToFlow360Case,
  artifactToFlow360Folder,
  artifactToFlow360Geometry,
  artifactToFlow360SurfaceMesh,
  artifactToFlow360VolumeMesh,
  collectThreadFilamentGeometryArtifacts,
  type ThreadFilamentGeometryArtifact,
} from "./ThreadV5FilamentArtifacts";
import {
  DEFAULT_FILAMENT_PRIMITIVE_SPEC,
  ThreadV5FilamentGeometryPreview,
  createFilamentPreviewRun,
  type FilamentMotionMode,
  type FilamentPrimitiveKind,
  type FilamentPrimitiveSpec,
  type FilamentWakeMode,
} from "./ThreadV5FilamentGeometryPreview";

const FOLDER_PAGE_SIZE = 80;
const FILAMENT_PIN_STORAGE_PREFIX = "thread:v5:filament-models";

type FilamentSourceMode = "primitive" | "project" | "test";

const CANONICAL_FILAMENT_TEST_CASES: ThreadFilamentGeometryArtifact[] = [
  {
    id: "canonical-filament-swept-wing",
    name: "Canonical swept wing",
    kind: "demo",
    kindLabel: "Filament fixture",
    sourceLabel: "Canonical test case",
    status: "ready",
    updatedAt: null,
    flow360ProjectId: null,
    flow360GeometryId: null,
    flow360SurfaceMeshId: null,
    flow360VolumeMeshId: null,
    flow360CaseId: "filament-canonical-swept-wing",
    panelCount: 2_400,
    storageBytes: null,
    provenanceNodeIds: [],
    rowIds: [],
    previewCaseId: "sweptwing",
  },
  {
    id: "canonical-filament-nacelle",
    name: "Canonical nacelle",
    kind: "demo",
    kindLabel: "Filament fixture",
    sourceLabel: "Canonical test case",
    status: "ready",
    updatedAt: null,
    flow360ProjectId: null,
    flow360GeometryId: null,
    flow360SurfaceMeshId: null,
    flow360VolumeMeshId: null,
    flow360CaseId: "filament-canonical-nacelle",
    panelCount: 480,
    storageBytes: null,
    provenanceNodeIds: [],
    rowIds: [],
    previewCaseId: "nacelle",
  },
];

function statusLabel(value: string | null | undefined): string {
  return value ? value.replace(/[_-]+/g, " ") : "unknown";
}

function dateLabel(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(date);
}

function caseCount(folder: Flow360Folder | null): number {
  return folder?.statistics?.Case?.count ?? 0;
}

function geometryCount(folder: Flow360Folder | null): number {
  return folder?.statistics?.Geometry?.count ?? 0;
}

function riskClass(risk: PanelRunRisk): string {
  return `is-${risk}`;
}

function entityMeta(entity: Flow360Geometry | Flow360SurfaceMesh | Flow360VolumeMesh | Flow360Case): string {
  const parts = [statusLabel(entity.status), entity.storageSize ? formatBytes(entity.storageSize) : "", dateLabel(entity.updatedAt ?? entity.createdAt)];
  return parts.filter(Boolean).join(" · ");
}

function makeResolvedCase(caseId: string | null, fullConfigName: string | null | undefined, volumeMeshId: string | null | undefined, projectId: string | null | undefined): Flow360Case | null {
  if (!caseId) return null;
  return {
    id: caseId,
    name: fullConfigName ?? caseId,
    volumeMeshId: volumeMeshId ?? undefined,
    projectId: projectId ?? undefined,
  };
}

function estimateMetricCards(estimate: Flow360PanelRunEstimate) {
  return [
    { label: "Source panels", value: formatPanels(estimate.sourcePanels), detail: estimate.confidence },
    { label: "Source dense matrix", value: formatGigabytes(estimate.sourceDenseMatrixGb), detail: "direct solve memory" },
    { label: "Wake solve", value: `${estimate.wakeIterations} iters`, detail: "relaxation passes" },
    { label: "Largest payload", value: formatBytes(estimate.storageBytes), detail: "Flow360 metadata" },
  ];
}

function sessionItem(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function parsePanelTarget(value: string): number | null {
  const parsed = Number(value.replace(/[,_\s]/g, ""));
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null;
}

function formatRatio(value: number | null | undefined): string {
  if (!value) return "unknown";
  if (value < 1.05) return "1×";
  return `${value.toFixed(value >= 10 ? 0 : 1)}×`;
}

function updateInputValue(inputs: FilamentInputControl[], key: string, value: number): FilamentInputControl[] {
  return inputs.map((input) => input.key === key ? { ...input, value } : input);
}

function updateInputPinned(inputs: FilamentInputControl[], key: string, pinned: boolean): FilamentInputControl[] {
  return inputs.map((input) => input.key === key ? { ...input, pinned } : input);
}

function primitiveKindLabel(kind: FilamentPrimitiveKind): string {
  if (kind === "delta-wing") return "Delta wing";
  if (kind === "nacelle") return "Nacelle / duct";
  if (kind === "straight-wing") return "Straight wing";
  return "Swept wing";
}

function wakeModeLabel(mode: FilamentWakeMode): string {
  return mode === "unsteady" ? "Unsteady / shedding" : "Steady";
}

function motionModeLabel(mode: FilamentMotionMode): string {
  return mode === "pitch-oscillation" ? "Oscillating pitch" : "Fixed wing/body";
}

export function Flow360ImportSurface({
  data,
  projectId,
  projectName,
  branch,
  persistedProject,
  onOpenView,
}: SurfaceProps) {
  const [folderSearch, setFolderSearch] = useState("");
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(null);
  const [sourceMode, setSourceMode] = useState<FilamentSourceMode>("primitive");
  const [addGeometryOpen, setAddGeometryOpen] = useState(false);
  const [caseLookupDraft, setCaseLookupDraft] = useState("");
  const [caseLookupId, setCaseLookupId] = useState<string | null>(null);
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [backendTarget, setBackendTarget] = useState<FilamentBackendTarget>("webgpu");
  const [decimationStrategy, setDecimationStrategy] = useState<FilamentDecimationStrategy>("smart");
  const [wakeIterations, setWakeIterations] = useState(15);
  const [targetPanelsDraft, setTargetPanelsDraft] = useState("");
  const [modelName, setModelName] = useState("Filament low-fidelity prior");
  const [modelInputs, setModelInputs] = useState<FilamentInputControl[]>(() => extractFilamentInputs(null));
  const [primitiveSpec, setPrimitiveSpec] = useState<FilamentPrimitiveSpec>(DEFAULT_FILAMENT_PRIMITIVE_SPEC);
  const [pinStatus, setPinStatus] = useState<string | null>(null);
  const flow360Email = typeof window === "undefined" ? null : getFlow360UserEmail();
  const hasBrowserFlow360Token = typeof window === "undefined" ? false : Boolean(getFlow360AccessToken());
  const flow360ApiKeyStored = sessionItem("flow360_api_key_stored") === "true";

  const authQuery = useQuery({
    queryKey: ["flow360", "auth-status"],
    queryFn: checkAuthStatus,
    retry: false,
    staleTime: 60_000,
  });
  const hasStoredFlow360Key = Boolean(authQuery.data?.has_stored_key);
  const authenticated = authQuery.data?.authenticated || Boolean(flow360Email) || hasBrowserFlow360Token || hasStoredFlow360Key;
  const threadGeometryArtifacts = useMemo(() => collectThreadFilamentGeometryArtifacts(data), [data]);
  const fixtureWorkbench = data.source === "fixture" || data.source === "mock-fallback" || data.project?.id === "local-thread-project";
  const projectGeometryArtifacts = useMemo(() => {
    if (fixtureWorkbench) return [];
    return threadGeometryArtifacts.filter((artifact) => artifact.kind !== "demo");
  }, [fixtureWorkbench, threadGeometryArtifacts]);
  const testGeometryArtifacts = useMemo(() => {
    const threadFixtures = fixtureWorkbench ? threadGeometryArtifacts : threadGeometryArtifacts.filter((artifact) => artifact.kind === "demo");
    const byId = new Map<string, ThreadFilamentGeometryArtifact>();
    [...threadFixtures, ...CANONICAL_FILAMENT_TEST_CASES].forEach((artifact) => byId.set(artifact.id, artifact));
    return Array.from(byId.values());
  }, [fixtureWorkbench, threadGeometryArtifacts]);
  const visibleGeometryArtifacts = sourceMode === "project" ? projectGeometryArtifacts : sourceMode === "test" ? testGeometryArtifacts : [];
  const selectedArtifact = useMemo(() => {
    if (sourceMode === "primitive") return null;
    return visibleGeometryArtifacts.find((artifact) => artifact.id === selectedArtifactId) ?? visibleGeometryArtifacts[0] ?? null;
  }, [selectedArtifactId, sourceMode, visibleGeometryArtifacts]);
  const primitiveRun = useMemo(() => createFilamentPreviewRun(backendTarget, primitiveSpec), [backendTarget, primitiveSpec]);
  const primitiveArtifact = useMemo<ThreadFilamentGeometryArtifact>(() => ({
    id: "primitive-filament-sandbox",
    name: primitiveSpec.kind.replace(/-/g, " ").replace(/^\w/, (first) => first.toUpperCase()),
    kind: "demo",
    kindLabel: "Primitive",
    sourceLabel: `${primitiveSpec.wakeMode} Filament sandbox`,
    status: "draft",
    updatedAt: null,
    flow360ProjectId: null,
    flow360GeometryId: null,
    flow360SurfaceMeshId: "primitive-filament-surface",
    flow360VolumeMeshId: null,
    flow360CaseId: null,
    panelCount: primitiveRun.summary.bodyCells,
    storageBytes: null,
    provenanceNodeIds: [],
    rowIds: [],
    previewCaseId: primitiveSpec.kind === "nacelle" ? "nacelle" : "sweptwing",
  }), [primitiveRun.summary.bodyCells, primitiveSpec.kind, primitiveSpec.wakeMode]);
  const activeArtifact = sourceMode === "primitive" ? primitiveArtifact : selectedArtifact;

  useEffect(() => {
    if (sourceMode === "primitive") return;
    if (!visibleGeometryArtifacts.length) {
      if (selectedArtifactId) setSelectedArtifactId(null);
      return;
    }
    if (!selectedArtifactId || !visibleGeometryArtifacts.some((artifact) => artifact.id === selectedArtifactId)) {
      setSelectedArtifactId(visibleGeometryArtifacts[0]?.id ?? null);
    }
  }, [selectedArtifactId, sourceMode, visibleGeometryArtifacts]);

  const foldersQuery = useQuery({
    queryKey: ["flow360", "folders", folderSearch],
    queryFn: () => fetchFlow360Folders({ page: 0, size: FOLDER_PAGE_SIZE, search: folderSearch.trim() || undefined }),
    enabled: authenticated && addGeometryOpen,
    retry: false,
    staleTime: 30_000,
  });
  const folders = foldersQuery.data?.projects ?? [];

  useEffect(() => {
    if (!selectedFolderId && folders[0]) setSelectedFolderId(folders[0].id);
  }, [folders, selectedFolderId]);

  const fullConfigQuery = useQuery({
    queryKey: ["flow360", "case-full-config", caseLookupId],
    queryFn: () => fetchFullConfig(caseLookupId!),
    enabled: authenticated && Boolean(caseLookupId),
    retry: false,
    staleTime: 60_000,
  });

  const resolvedFolderId = activeArtifact?.flow360ProjectId ?? null;
  const addGeometryFolderId = selectedFolderId ?? fullConfigQuery.data?.project_id ?? null;
  const selectedFolder = useMemo(() => {
    const artifactFolder = artifactToFlow360Folder(activeArtifact);
    if (!resolvedFolderId) return artifactFolder;
    const found = folders.find((folder) => folder.id === resolvedFolderId);
    if (found) return found;
    if (artifactFolder?.id === resolvedFolderId) return artifactFolder;
    return null;
  }, [activeArtifact, folders, resolvedFolderId]);

  const geometriesQuery = useQuery({
    queryKey: ["flow360", "geometries", resolvedFolderId],
    queryFn: () => fetchFlow360Geometries(resolvedFolderId ?? undefined),
    enabled: authenticated && Boolean(resolvedFolderId),
    retry: false,
  });
  const surfaceMeshesQuery = useQuery({
    queryKey: ["flow360", "surface-meshes", resolvedFolderId],
    queryFn: () => fetchFlow360SurfaceMeshes({ folderId: resolvedFolderId ?? undefined }),
    enabled: authenticated && Boolean(resolvedFolderId),
    retry: false,
  });
  const volumeMeshesQuery = useQuery({
    queryKey: ["flow360", "volume-meshes", resolvedFolderId],
    queryFn: () => fetchFlow360VolumeMeshes({ folderId: resolvedFolderId ?? undefined }),
    enabled: authenticated && Boolean(resolvedFolderId),
    retry: false,
  });
  const casesQuery = useQuery({
    queryKey: ["flow360", "cases", resolvedFolderId],
    queryFn: () => fetchFlow360Cases({ folderId: resolvedFolderId ?? undefined, limit: 500 }),
    enabled: authenticated && Boolean(resolvedFolderId),
    retry: false,
  });

  const geometries = geometriesQuery.data ?? [];
  const surfaceMeshes = surfaceMeshesQuery.data ?? [];
  const volumeMeshes = volumeMeshesQuery.data ?? [];
  const cases = casesQuery.data ?? [];
  const activeCaseId = selectedCaseId ?? activeArtifact?.flow360CaseId ?? cases[0]?.id ?? null;
  const resolvedCase = useMemo(() => {
    const listed = activeCaseId ? cases.find((item) => item.id === activeCaseId) : null;
    return listed ?? artifactToFlow360Case(activeArtifact) ?? makeResolvedCase(activeCaseId, null, activeArtifact?.flow360VolumeMeshId, activeArtifact?.flow360ProjectId);
  }, [activeArtifact, activeCaseId, cases]);
  const selectedVolumeMesh = useMemo(() => {
    const artifactMesh = artifactToFlow360VolumeMesh(activeArtifact);
    const volumeId = artifactMesh?.id ?? resolvedCase?.volumeMeshId ?? resolvedCase?.parentId;
    return volumeMeshes.find((mesh) => mesh.id === volumeId) ?? artifactMesh ?? volumeMeshes[0] ?? null;
  }, [activeArtifact, resolvedCase?.parentId, resolvedCase?.volumeMeshId, volumeMeshes]);
  const selectedSurfaceMesh = useMemo(() => {
    const artifactMesh = artifactToFlow360SurfaceMesh(activeArtifact);
    const surfaceId = artifactMesh?.id ?? selectedVolumeMesh?.surfaceMeshId ?? selectedVolumeMesh?.parentId;
    return surfaceMeshes.find((mesh) => mesh.id === surfaceId) ?? artifactMesh ?? surfaceMeshes[0] ?? null;
  }, [activeArtifact, selectedVolumeMesh?.parentId, selectedVolumeMesh?.surfaceMeshId, surfaceMeshes]);
  const selectedGeometry = useMemo(() => {
    const artifactGeometry = artifactToFlow360Geometry(activeArtifact);
    const geometryId = artifactGeometry?.id ?? selectedSurfaceMesh?.geometryId ?? selectedSurfaceMesh?.parentId;
    return geometries.find((geometry) => geometry.id === geometryId) ?? artifactGeometry ?? geometries[0] ?? null;
  }, [activeArtifact, geometries, selectedSurfaceMesh?.geometryId, selectedSurfaceMesh?.parentId]);
  const visibleGeometries = useMemo(() => selectedGeometry ? [selectedGeometry, ...geometries.filter((item) => item.id !== selectedGeometry.id)].slice(0, 4) : geometries.slice(0, 4), [geometries, selectedGeometry]);
  const visibleSurfaceMeshes = useMemo(() => selectedSurfaceMesh ? [selectedSurfaceMesh, ...surfaceMeshes.filter((item) => item.id !== selectedSurfaceMesh.id)].slice(0, 4) : surfaceMeshes.slice(0, 4), [selectedSurfaceMesh, surfaceMeshes]);
  const visibleVolumeMeshes = useMemo(() => selectedVolumeMesh ? [selectedVolumeMesh, ...volumeMeshes.filter((item) => item.id !== selectedVolumeMesh.id)].slice(0, 4) : volumeMeshes.slice(0, 4), [selectedVolumeMesh, volumeMeshes]);

  const artifactFullConfig = useMemo(() => {
    if (!activeArtifact?.panelCount) return null;
    return {
      case_name: activeArtifact.name,
      project_id: activeArtifact.flow360ProjectId ?? null,
      volume_mesh_id: activeArtifact.flow360VolumeMeshId ?? null,
      solver_version: null,
      config: {
        meshStatistics: {
          surfaceFaceCount: activeArtifact.panelCount,
        },
      },
    };
  }, [activeArtifact]);
  const estimateFullConfig = artifactFullConfig;

  const estimate = useMemo(() => estimateFlow360PanelRunDifficulty({
    folder: selectedFolder,
    geometry: selectedGeometry,
    surfaceMesh: selectedSurfaceMesh,
    volumeMesh: selectedVolumeMesh,
    case: resolvedCase,
    fullConfig: estimateFullConfig,
    caseId: activeCaseId,
    wakeIterations,
  }), [activeCaseId, estimateFullConfig, resolvedCase, selectedFolder, selectedGeometry, selectedSurfaceMesh, selectedVolumeMesh, wakeIterations]);

  const recommendedTargetPanels = backendTarget === "cuda" ? estimate.cuda.targetPanels : estimate.mac.targetPanels;

  useEffect(() => {
    setModelInputs(extractFilamentInputs(estimateFullConfig?.config ?? null));
  }, [activeCaseId, estimateFullConfig?.config]);

  useEffect(() => {
    setTargetPanelsDraft(recommendedTargetPanels ? String(recommendedTargetPanels) : "");
  }, [activeCaseId, backendTarget, estimate.sourcePanels, recommendedTargetPanels]);

  const filamentPlan = useMemo(() => createFilamentModelPlan({
    estimate,
    backend: backendTarget,
    surfaceMesh: selectedSurfaceMesh,
    case: resolvedCase,
    targetPanels: parsePanelTarget(targetPanelsDraft),
    decimationStrategy,
    wakeIterations,
    inputControls: modelInputs,
  }), [backendTarget, decimationStrategy, estimate, modelInputs, resolvedCase, selectedSurfaceMesh, targetPanelsDraft, wakeIterations]);

  const importMutation = useMutation({
    mutationFn: async () => {
      const folderId = addGeometryFolderId;
      if (!folderId) throw new Error("Select a Flow360 project or resolve a case ID first.");
      const importFolder = folders.find((folder) => folder.id === folderId);
      return importFlow360Data({
        folder_id: folderId,
        folder_name: importFolder?.name ?? fullConfigQuery.data?.project_id ?? folderId,
        project_id: persistedProject ? projectId : undefined,
        project_name: projectName,
        branch,
      });
    },
    onSuccess: (result) => setImportJobId(result.job_id),
  });

  const importProgressQuery = useQuery({
    queryKey: ["flow360", "import-progress", importJobId],
    queryFn: () => fetchImportProgress(importJobId!),
    enabled: Boolean(importJobId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "completed" || status === "failed" ? false : 2_000;
    },
  });

  const resolveCase = () => {
    const value = caseLookupDraft.trim();
    if (!value) return;
    setCaseLookupId(value);
    setSelectedCaseId(value);
    setSelectedFolderId(null);
  };

  const inspectCase = (item: Flow360Case) => {
    setSelectedCaseId(item.id);
    setCaseLookupId(item.id);
  };

  const pinFilamentModel = () => {
    if (typeof window === "undefined") return;
    const storageKey = `${FILAMENT_PIN_STORAGE_PREFIX}:${projectId}:${branch}`;
    const pinnedModel = {
      id: `filament-${Date.now()}`,
      name: modelName.trim() || "Filament low-fidelity prior",
      createdAt: new Date().toISOString(),
      threadProjectId: projectId,
      branch,
      threadArtifact: activeArtifact,
      flow360: {
        authSource: hasStoredFlow360Key ? "thread_profile_key" : hasBrowserFlow360Token ? "browser_session" : flow360ApiKeyStored ? "profile_key_captured" : "not_connected",
        projectId: resolvedFolderId,
        caseId: resolvedCase?.id ?? activeCaseId,
        surfaceMeshId: selectedSurfaceMesh?.id ?? null,
        volumeMeshId: selectedVolumeMesh?.id ?? null,
      },
      plan: filamentPlan,
    };
    let current: unknown[] = [];
    try {
      const parsed = JSON.parse(window.localStorage.getItem(storageKey) ?? "[]");
      current = Array.isArray(parsed) ? parsed : [];
    } catch {
      current = [];
    }
    const models = Array.isArray(current) ? current : [];
    window.localStorage.setItem(storageKey, JSON.stringify([pinnedModel, ...models].slice(0, 12)));
    setPinStatus(`Pinned “${pinnedModel.name}” for Tailor on ${filamentPlan.backendLabel}.`);
  };

  const folderBusy = foldersQuery.isLoading || geometriesQuery.isLoading || surfaceMeshesQuery.isLoading || volumeMeshesQuery.isLoading || casesQuery.isLoading;
  const progress = importProgressQuery.data;
  const canImport = Boolean(addGeometryFolderId) && !importMutation.isPending;
  const sourceCount = sourceMode === "primitive" ? 1 : visibleGeometryArtifacts.length;
  const selectSourceMode = (mode: FilamentSourceMode) => {
    setSourceMode(mode);
    setSelectedArtifactId(null);
    setSelectedCaseId(null);
    setCaseLookupId(null);
  };
  const sourceOption = (mode: FilamentSourceMode, label: string, count: number | string) => (
    <button className={`v5-mini-option ${sourceMode === mode ? "is-selected" : ""}`} type="button" onClick={() => selectSourceMode(mode)}>
      {label}<small>{count}</small>
    </button>
  );

  return (
    <main className="v5-main v5-flow360-import">
      <header className="v5-page-header v5-filament-hero">
        <div>
          <span className="v5-eyebrow">Thread V5 · Filament model</span>
          <h1>Build and test Filament panel models</h1>
          <p>Use project-owned geometry when it exists, canonical test cases when it does not, or define a primitive geometry and wake model directly.</p>
        </div>
        <div className="v5-filament-actions">
          <button className="v5-primary-button" type="button" onClick={() => setAddGeometryOpen((open) => !open)}>
            <Icons.Plus size={13} />Add Geometry
          </button>
        </div>
      </header>

      <section className="v5-flow360-layout">
        <aside className="v5-import-panel v5-import-browser">
          <div className="v5-import-panel-header">
            <div>
              <span className="v5-eyebrow">Filament source</span>
              <h2>{sourceMode === "primitive" ? "Primitive builder" : sourceMode === "test" ? "Test cases" : "Project geometries"}</h2>
            </div>
            <span className="v5-chip">{sourceCount}</span>
          </div>
          <div className="v5-filament-source-tabs">
            {sourceOption("primitive", "Primitive", "live")}
            {sourceOption("project", "Project", projectGeometryArtifacts.length)}
            {sourceOption("test", "Test cases", testGeometryArtifacts.length)}
          </div>
          <p className="v5-import-scope-note">
            Project mode only shows geometries, meshes, and Flow360 cases attached to this Thread project. Canonical examples live behind Test cases; Primitive is an editable Filament sandbox.
          </p>
          {sourceMode === "primitive" ? (
            <FilamentPrimitiveEditor spec={primitiveSpec} onChange={setPrimitiveSpec} />
          ) : visibleGeometryArtifacts.length ? (
            <div className="v5-import-list">
              {visibleGeometryArtifacts.map((artifact) => (
                <ThreadGeometryCard
                  key={artifact.id}
                  artifact={artifact}
                  selected={selectedArtifact?.id === artifact.id}
                  onSelect={() => {
                    setSelectedArtifactId(artifact.id);
                    setSelectedCaseId(null);
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="v5-import-empty">
              <Icons.Cube size={20} />
              <strong>{sourceMode === "project" ? "No project geometries yet" : "No test cases available"}</strong>
              <p>{sourceMode === "project" ? "Add a Flow360 project, upload a mesh, or attach a geometry to this Thread project before creating a project-backed Filament model." : "Use the Primitive builder while canonical cases are unavailable."}</p>
            </div>
          )}
          {addGeometryOpen ? (
            <div className="v5-add-geometry-panel">
              <div className="v5-import-panel-header is-compact">
                <div>
                  <span className="v5-eyebrow">Add Geometry</span>
                  <h3>Import into Thread first</h3>
                </div>
                {folderBusy ? <span className="v5-chip">loading</span> : null}
              </div>
              <p>Uploads and Flow360 sources are staged as Thread artifacts before Filament can use them.</p>
              <div className="v5-add-geometry-actions">
                <button className="v5-text-button" type="button" disabled title="Thread artifact upload endpoint is next.">
                  <Icons.Asset size={13} />Upload mesh/geometry
                </button>
                <button className="v5-primary-button" type="button" onClick={() => importMutation.mutate()} disabled={!canImport}>
                  <Icons.Plus size={13} />Add selected Flow360 project
                </button>
              </div>
              <div className="v5-case-lookup is-compact">
                <label>
                  <span>Resolve Flow360 case ID</span>
                  <input value={caseLookupDraft} onChange={(event) => setCaseLookupDraft(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") resolveCase(); }} placeholder="case-..." />
                </label>
                <button className="v5-text-button" type="button" onClick={resolveCase} disabled={!caseLookupDraft.trim()}>
                  <Icons.Search size={13} />Resolve
                </button>
              </div>
              {fullConfigQuery.isError ? (
                <div className="v5-import-empty is-error is-compact">
                  <Icons.Info size={18} />
                  <strong>Case lookup failed</strong>
                  <p>{fullConfigQuery.error instanceof Error ? fullConfigQuery.error.message : "Could not fetch Flow360 case config."}</p>
                </div>
              ) : fullConfigQuery.data?.project_id ? (
                <p className="v5-add-geometry-resolved">Resolved project <code>{fullConfigQuery.data.project_id}</code>. Add it to Thread before using it as Filament geometry.</p>
              ) : null}
              <label className="v5-import-search">
                <Icons.Search size={13} />
                <input value={folderSearch} onChange={(event) => setFolderSearch(event.target.value)} placeholder="Search Flow360 projects to add..." />
              </label>
              {!authenticated ? (
                <div className="v5-import-empty">
                  <Icons.Lock size={20} />
                  <strong>Flow360 connector needed</strong>
                  <p>Mint the Flow360 credential once, then Thread can import geometry, meshes, cases, and provenance.</p>
                </div>
              ) : foldersQuery.isError ? (
                <div className="v5-import-empty is-error">
                  <Icons.Info size={20} />
                  <strong>Could not load Flow360 projects</strong>
                  <p>{foldersQuery.error instanceof Error ? foldersQuery.error.message : "Unknown Flow360 browser error"}</p>
                </div>
              ) : (
                <div className="v5-import-list is-compact">
                  {folders.map((folder) => (
                    <button
                      key={folder.id}
                      className={`v5-import-card ${selectedFolderId === folder.id ? "is-selected" : ""}`}
                      type="button"
                      onClick={() => {
                        setSelectedFolderId(folder.id);
                        setSelectedCaseId(null);
                        setCaseLookupId(null);
                      }}
                    >
                      <span className="v5-import-card-icon"><Icons.Folder size={15} /></span>
                      <span>
                        <strong>{folder.name}</strong>
                        <small>{caseCount(folder)} cases · {geometryCount(folder)} geometries · {statusLabel(folder.rootItemDisplayStatus ?? folder.rootItemStatus)}</small>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </aside>

        <section className="v5-import-panel v5-import-source">
          <ThreadV5FilamentGeometryPreview
            source={activeArtifact}
            backendTarget={backendTarget}
            primitiveSpec={sourceMode === "primitive" ? primitiveSpec : null}
            wakeIterations={wakeIterations}
          />

          <div className="v5-thread-artifact-details">
            <div className="v5-import-panel-header is-compact">
              <div>
                <span className="v5-eyebrow">Selected Thread artifact</span>
                <h3>{activeArtifact?.name ?? "Waiting for geometry"}</h3>
              </div>
              <span className="v5-chip">{activeArtifact?.kindLabel ?? "none"}</span>
            </div>
            <dl>
              <div><dt>Source</dt><dd>{activeArtifact?.sourceLabel ?? "No artifact"}</dd></div>
              <div><dt>Panels</dt><dd>{formatPanels(activeArtifact?.panelCount)}</dd></div>
              <div><dt>Rows</dt><dd>{activeArtifact?.rowIds.length ?? 0}</dd></div>
              <div><dt>Flow360 case</dt><dd>{activeArtifact?.flow360CaseId ?? "not linked"}</dd></div>
            </dl>
          </div>

          <div className="v5-entity-grid">
            <EntityStack title="Geometry" icon={<Icons.Cube size={14} />} items={visibleGeometries} selectedId={selectedGeometry?.id ?? null} />
            <EntityStack title="Surface mesh" icon={<Icons.Mesh size={14} />} items={visibleSurfaceMeshes} selectedId={selectedSurfaceMesh?.id ?? null} />
            <EntityStack title="Volume mesh" icon={<Icons.Layers size={14} />} items={visibleVolumeMeshes} selectedId={selectedVolumeMesh?.id ?? null} />
          </div>

          <div className="v5-import-cases">
            <div className="v5-import-panel-header is-compact">
              <div>
                <span className="v5-eyebrow">Cases</span>
                <h3>{cases.length ? `${cases.length} available` : "No listed cases yet"}</h3>
              </div>
            </div>
            <div className="v5-case-list">
              {cases.slice(0, 12).map((item) => (
                <button key={item.id} className={`v5-case-row ${activeCaseId === item.id ? "is-selected" : ""}`} type="button" onClick={() => inspectCase(item)}>
                  <span><strong>{item.name}</strong><small>{item.id}</small></span>
                  <em>{entityMeta(item)}</em>
                </button>
              ))}
              {!cases.length ? (
                <div className="v5-import-empty">
                  <Icons.Search size={18} />
                  <strong>{resolvedFolderId ? "No linked cases returned" : "No Flow360 case linked"}</strong>
                  <p>Add a Flow360 case or project to Thread first, then the case can feed the Filament model.</p>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <aside className="v5-import-panel v5-estimator-panel">
          <div className="v5-import-panel-header">
            <div>
              <span className="v5-eyebrow">Hardness estimate</span>
              <h2>{resolvedCase?.name ?? activeArtifact?.name ?? selectedFolder?.name ?? "Select a geometry"}</h2>
            </div>
            <span className={`v5-chip is-${estimate.confidence}`}>{estimate.confidence}</span>
          </div>
          <div className="v5-estimate-metrics">
            {estimateMetricCards(estimate).map((metric) => (
              <div key={metric.label} className="v5-estimate-metric">
                <span>{metric.label}</span>
                <strong>{metric.value}</strong>
                <small>{metric.detail}</small>
              </div>
            ))}
          </div>
          <FilamentModelBuilder
            backendTarget={backendTarget}
            decimationStrategy={decimationStrategy}
            modelName={modelName}
            targetPanelsDraft={targetPanelsDraft}
            wakeIterations={wakeIterations}
            inputs={modelInputs}
            plan={filamentPlan}
            pinStatus={pinStatus}
            onBackendChange={setBackendTarget}
            onDecimationChange={setDecimationStrategy}
            onModelNameChange={setModelName}
            onTargetPanelsChange={setTargetPanelsDraft}
            onWakeIterationsChange={setWakeIterations}
            onInputValueChange={(key, value) => setModelInputs((current) => updateInputValue(current, key, value))}
            onInputPinnedChange={(key, pinned) => setModelInputs((current) => updateInputPinned(current, key, pinned))}
            onPin={pinFilamentModel}
          />
          <div className="v5-risk-grid">
            <RiskCard title="WebGPU / local" target={estimate.mac} />
            <RiskCard title="CUDA dev GPU" target={estimate.cuda} />
          </div>
          <div className="v5-estimate-notes">
            {estimate.facts.map((fact) => <p key={fact}><Icons.Info size={12} />{fact}</p>)}
            {estimate.warnings.map((warning) => <p key={warning} className="is-warning"><Icons.Info size={12} />{warning}</p>)}
          </div>
          <div className="v5-import-action-card">
            <strong>Thread artifact target</strong>
            <p>{persistedProject ? projectName : "New/local Thread project"} · branch {branch}</p>
            {progress ? (
              <div className="v5-import-progress">
                <span><b>{progress.status}</b>{progress.total_cases ? ` · ${progress.imported_cases}/${progress.total_cases} cases` : ""}</span>
                <i style={{ width: `${progress.progress_pct}%` }} />
              </div>
            ) : importMutation.isError ? (
              <p className="v5-import-error">{importMutation.error instanceof Error ? importMutation.error.message : "Import failed"}</p>
            ) : (
              <p>Add Geometry creates Thread nodes for geometry, meshes, cases, results, and provenance. The pinned Filament model is the low-fidelity prior Tailor will run against.</p>
            )}
            {progress?.status === "completed" ? (
              <button className="v5-primary-button" type="button" onClick={() => onOpenView("data")}><Icons.Table size={13} />Open imported data</button>
            ) : null}
          </div>
        </aside>
      </section>
    </main>
  );
}

function ThreadGeometryCard({
  artifact,
  selected,
  onSelect,
}: {
  artifact: ThreadFilamentGeometryArtifact;
  selected: boolean;
  onSelect: () => void;
}) {
  const icon = artifact.kind === "surface_mesh" ? <Icons.Mesh size={15} /> : artifact.kind === "volume_mesh" ? <Icons.Layers size={15} /> : artifact.kind === "case" ? <Icons.Flowfield size={15} /> : <Icons.Cube size={15} />;
  const detail = [
    artifact.kindLabel,
    artifact.panelCount ? `${formatPanels(artifact.panelCount)} panels` : null,
    artifact.flow360CaseId ? "Flow360 case" : null,
    artifact.rowIds.length ? `${artifact.rowIds.length} rows` : null,
  ].filter(Boolean).join(" · ");
  return (
    <button className={`v5-import-card ${selected ? "is-selected" : ""}`} type="button" onClick={onSelect}>
      <span className="v5-import-card-icon">{icon}</span>
      <span>
        <strong>{artifact.name}</strong>
        <small>{artifact.sourceLabel} · {detail || "geometry artifact"}</small>
      </span>
    </button>
  );
}

type PrimitiveNumberKey = "alphaDeg" | "betaDeg" | "camber" | "chord" | "dihedralDeg" | "oscillationAmplitudeDeg" | "oscillationFrequency" | "span" | "sweepDeg" | "wakeLength";

function primitiveDefaultsForKind(nextKind: FilamentPrimitiveKind, current: FilamentPrimitiveSpec): FilamentPrimitiveSpec {
  const wake = {
    alphaDeg: current.alphaDeg,
    betaDeg: current.betaDeg,
    wakeLength: current.wakeLength,
    wakeMode: current.wakeMode,
    motionMode: current.motionMode,
    oscillationAmplitudeDeg: current.oscillationAmplitudeDeg,
    oscillationFrequency: current.oscillationFrequency,
  };
  if (nextKind === "nacelle") return { ...current, ...wake, kind: nextKind, span: 1.1, chord: 1.8, sweepDeg: 0, dihedralDeg: 0, camber: 0.02 };
  if (nextKind === "delta-wing") return { ...current, ...wake, kind: nextKind, span: 2.2, chord: 1.2, sweepDeg: 45, dihedralDeg: 1, camber: 0.045 };
  if (nextKind === "straight-wing") return { ...current, ...wake, kind: nextKind, span: 2.4, chord: 0.95, sweepDeg: 0, dihedralDeg: 3, camber: 0.05 };
  return { ...current, ...DEFAULT_FILAMENT_PRIMITIVE_SPEC, ...wake, kind: nextKind };
}

function FilamentPrimitiveEditor({
  spec,
  onChange,
}: {
  spec: FilamentPrimitiveSpec;
  onChange: (spec: FilamentPrimitiveSpec) => void;
}) {
  const numberInput = (key: PrimitiveNumberKey, label: string, min: number, max: number, step: number, suffix?: string) => (
    <label>
      <span>{label}</span>
      <div className="v5-primitive-number">
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={spec[key]}
          onChange={(event) => {
            const nextValue = Number(event.target.value);
            if (Number.isFinite(nextValue)) onChange({ ...spec, [key]: nextValue });
          }}
        />
        {suffix ? <em>{suffix}</em> : null}
      </div>
    </label>
  );

  return (
    <div className="v5-primitive-builder">
      <div className="v5-primitive-grid">
        <label>
          <span>Primitive geometry</span>
          <select
            value={spec.kind}
            onChange={(event) => onChange(primitiveDefaultsForKind(event.target.value as FilamentPrimitiveKind, spec))}
          >
            {(["swept-wing", "straight-wing", "delta-wing", "nacelle"] as FilamentPrimitiveKind[]).map((kindOption) => (
              <option key={kindOption} value={kindOption}>{primitiveKindLabel(kindOption)}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Wake model</span>
          <select value={spec.wakeMode} onChange={(event) => onChange({ ...spec, wakeMode: event.target.value as FilamentWakeMode })}>
            {(["steady", "unsteady"] as FilamentWakeMode[]).map((modeOption) => (
              <option key={modeOption} value={modeOption}>{wakeModeLabel(modeOption)}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Wing / source motion</span>
          <select value={spec.motionMode} onChange={(event) => onChange({ ...spec, motionMode: event.target.value as FilamentMotionMode })}>
            {(["fixed", "pitch-oscillation"] as FilamentMotionMode[]).map((modeOption) => (
              <option key={modeOption} value={modeOption}>{motionModeLabel(modeOption)}</option>
            ))}
          </select>
        </label>
        {numberInput("span", spec.kind === "nacelle" ? "Diameter scale" : "Span", 0.6, 8, 0.05)}
        {numberInput("chord", spec.kind === "nacelle" ? "Body length" : "Root chord", 0.25, 4, 0.05)}
        {numberInput("sweepDeg", "Sweep", -35, 65, 1, "deg")}
        {numberInput("dihedralDeg", "Dihedral", -20, 20, 1, "deg")}
        {numberInput("camber", "Camber", -0.08, 0.16, 0.005)}
        {numberInput("alphaDeg", "Alpha", -15, 20, 0.25, "deg")}
        {numberInput("betaDeg", "Beta", -20, 20, 0.25, "deg")}
        {numberInput("wakeLength", "Filament / wake length", 0.2, 8, 0.05, "c")}
        {numberInput("oscillationAmplitudeDeg", "Oscillation amplitude", 0, 25, 0.5, "deg")}
        {numberInput("oscillationFrequency", "Oscillation frequency", 0.1, 5, 0.1, "cyc")}
      </div>
      <p>
        This is a Filament sandbox: controls regenerate the primitive panel mesh and wake immediately. The preview seeds the wake with freestream convection and uses solve iterations to relax it with bound/wake filament influence.
      </p>
    </div>
  );
}

function FilamentModelBuilder({
  backendTarget,
  decimationStrategy,
  modelName,
  targetPanelsDraft,
  wakeIterations,
  inputs,
  plan,
  pinStatus,
  onBackendChange,
  onDecimationChange,
  onModelNameChange,
  onTargetPanelsChange,
  onWakeIterationsChange,
  onInputValueChange,
  onInputPinnedChange,
  onPin,
}: {
  backendTarget: FilamentBackendTarget;
  decimationStrategy: FilamentDecimationStrategy;
  modelName: string;
  targetPanelsDraft: string;
  wakeIterations: number;
  inputs: FilamentInputControl[];
  plan: FilamentModelPlan;
  pinStatus: string | null;
  onBackendChange: (target: FilamentBackendTarget) => void;
  onDecimationChange: (strategy: FilamentDecimationStrategy) => void;
  onModelNameChange: (name: string) => void;
  onTargetPanelsChange: (value: string) => void;
  onWakeIterationsChange: (value: number) => void;
  onInputValueChange: (key: string, value: number) => void;
  onInputPinnedChange: (key: string, pinned: boolean) => void;
  onPin: () => void;
}) {
  const backendOption = (target: FilamentBackendTarget, label: string, detail: string) => (
    <button
      className={`v5-segment-option ${backendTarget === target ? "is-selected" : ""}`}
      type="button"
      onClick={() => onBackendChange(target)}
    >
      <strong>{label}</strong>
      <small>{detail}</small>
    </button>
  );
  const strategyOption = (strategy: FilamentDecimationStrategy, label: string) => (
    <button
      className={`v5-mini-option ${decimationStrategy === strategy ? "is-selected" : ""}`}
      type="button"
      onClick={() => onDecimationChange(strategy)}
    >
      {label}
    </button>
  );

  return (
    <div className="v5-filament-builder">
      <div className="v5-import-panel-header is-compact">
        <div>
          <span className="v5-eyebrow">Filament model</span>
          <h3>Mesh, backend, inputs</h3>
        </div>
      </div>

      <label className="v5-model-name">
        <span>Model name</span>
        <input value={modelName} onChange={(event) => onModelNameChange(event.target.value)} />
      </label>

      <div className="v5-backend-switch">
        {backendOption("webgpu", "WebGPU", "Mac/local preview")}
        {backendOption("cuda", "CUDA", "dev GPU solve")}
      </div>

      <div className="v5-mesh-plan-card">
        <div>
          <span className="v5-eyebrow">Surface mesh</span>
          <strong>{plan.sourceSurfaceMeshName ?? "No source mesh selected"}</strong>
          <p>{plan.meshStrategyDetail}</p>
        </div>
        <dl>
          <div><dt>Strategy</dt><dd>{plan.meshStrategyLabel}</dd></div>
          <div><dt>Target panels</dt><dd>{formatPanels(plan.targetPanels)}</dd></div>
          <div><dt>Downsample</dt><dd>{formatRatio(plan.decimationRatio)}</dd></div>
          <div><dt>Matrix</dt><dd>{formatGigabytes(plan.expectedDenseMatrixGb)}</dd></div>
        </dl>
      </div>

      <div className="v5-model-controls">
        <label>
          <span>Target panels</span>
          <input value={targetPanelsDraft} onChange={(event) => onTargetPanelsChange(event.target.value)} inputMode="numeric" />
        </label>
        <label>
          <span>Wake solve iterations</span>
          <input
            type="number"
            min={1}
            max={80}
            value={wakeIterations}
            onChange={(event) => onWakeIterationsChange(Math.max(1, Math.min(80, Number(event.target.value) || 1)))}
          />
        </label>
      </div>

      <div className="v5-decimation-options">
        {strategyOption("smart", "Smart")}
        {strategyOption("uniform", "Uniform")}
        {strategyOption("source", "Use source")}
      </div>

      <div className="v5-input-pin-grid">
        {inputs.map((input) => (
          <div key={input.key} className="v5-input-pin-row">
            <label>
              <span>{input.label}{input.unit ? ` (${input.unit})` : ""}</span>
              <input
                type="number"
                value={input.value}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  if (Number.isFinite(next)) onInputValueChange(input.key, next);
                }}
              />
            </label>
            <label className="v5-pin-toggle">
              <input
                type="checkbox"
                checked={input.pinned}
                onChange={(event) => onInputPinnedChange(input.key, event.target.checked)}
              />
              pin
            </label>
          </div>
        ))}
      </div>

      <div className="v5-filament-run-actions">
        <button className="v5-primary-button" type="button" onClick={onPin}>
          <Icons.Plus size={13} />Pin model for Tailor
        </button>
        <button className="v5-text-button" type="button" disabled title="Backend run endpoint is next; the pinned model spec is ready for it.">
          <Icons.Flowfield size={13} />Run in Tailor
        </button>
      </div>
      {pinStatus ? <p className="v5-pin-status">{pinStatus}</p> : null}
    </div>
  );
}

function EntityStack({
  title,
  icon,
  items,
  selectedId,
}: {
  title: string;
  icon: ReactNode;
  items: Array<Flow360Geometry | Flow360SurfaceMesh | Flow360VolumeMesh>;
  selectedId: string | null;
}) {
  return (
    <div className="v5-entity-stack">
      <header>{icon}<strong>{title}</strong><span>{items.length}</span></header>
      {items.length ? items.map((item) => (
        <div key={item.id} className={`v5-entity-card ${selectedId === item.id ? "is-selected" : ""}`}>
          <strong>{item.name}</strong>
          <small>{entityMeta(item)}</small>
        </div>
      )) : <p className="v5-muted">No {title.toLowerCase()} loaded.</p>}
    </div>
  );
}

function RiskCard({ title, target }: { title: string; target: Flow360PanelRunEstimate["mac"] }) {
  return (
    <div className={`v5-risk-card ${riskClass(target.risk)}`}>
      <span>{title}</span>
      <strong>{target.label}</strong>
      <p>{target.detail}</p>
      <dl>
        <div><dt>Target panels</dt><dd>{formatPanels(target.targetPanels)}</dd></div>
        <div><dt>Dense matrix</dt><dd>{formatGigabytes(target.denseMatrixGb)}</dd></div>
        <div><dt>Relative work</dt><dd>{target.relativeWork == null ? "unknown" : `${target.relativeWork.toFixed(target.relativeWork >= 10 ? 0 : 1)}×`}</dd></div>
      </dl>
    </div>
  );
}
